import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCatOpen, setIsCatOpen] = useState(false);
  const [isArticlesOpen, setIsArticlesOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileArticlesOpen, setMobileArticlesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const anyOpen = isMobileOpen || isCatOpen || isArticlesOpen;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen, isCatOpen, isArticlesOpen]);

  const closeAll = () => {
    setIsCatOpen(false);
    setIsArticlesOpen(false);
    setIsMobileOpen(false);
    setMobileCatOpen(false);
    setMobileArticlesOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-16">

          <Link to="/" onClick={closeAll} className="flex items-center gap-2 cursor-pointer flex-shrink-0" aria-label="Home">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-900 whitespace-nowrap">
              AllChinaBuy Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-0.5">
            <Link to="/" onClick={closeAll} className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>

            <Link to="/allchinabuy-spreadsheet-guide" onClick={closeAll} className="px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              Shopping Guide
            </Link>

            <div className="relative">
              <button
                onClick={() => { setIsCatOpen(!isCatOpen); setIsArticlesOpen(false); }}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                aria-expanded={isCatOpen}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isCatOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isCatOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsCatOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 z-50 max-h-[420px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer" onClick={() => setIsCatOpen(false)}>
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base text-primary-500`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => { setIsArticlesOpen(!isArticlesOpen); setIsCatOpen(false); }}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                aria-expanded={isArticlesOpen}
              >
                Articles
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${isArticlesOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isArticlesOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsArticlesOpen(false)} />
                  <div className="absolute top-full left-0 mt-2 w-80 bg-background-50 rounded-xl border border-background-200/70 py-2 z-50 max-h-[420px] overflow-y-auto">
                    <Link to="/allchinabuy-spreadsheet-guide" onClick={() => setIsArticlesOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer border-b border-background-100 mb-1 pb-2.5">
                      <i className="ri-book-open-line text-base"></i>
                      Complete Guide (Main)
                    </Link>
                    {articles.map((a) => (
                      <Link key={a.slug} to={`/article/${a.slug}`} onClick={() => setIsArticlesOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-article-line text-base text-primary-500"></i>
                        </span>
                        <span className="truncate">{a.title.length > 45 ? a.title.slice(0, 45) + '...' : a.title}</span>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="ml-2 px-5 py-2 text-sm font-semibold bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap">
              Buy It Now
            </a>
          </div>

          <button className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Menu">
            <i className={`text-xl ${isMobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-3 space-y-0.5">

            <Link to="/" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-home-4-line text-lg text-primary-500"></i></span>
              Home
            </Link>

            <Link to="/allchinabuy-spreadsheet-guide" onClick={closeAll} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <span className="w-6 h-6 flex items-center justify-center"><i className="ri-book-open-line text-lg text-primary-500"></i></span>
              Shopping Guide
            </Link>

            <div className="border-t border-background-200/60 my-2"></div>

            <button onClick={() => { setMobileCatOpen(!mobileCatOpen); setMobileArticlesOpen(false); }} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-grid-line text-lg text-primary-500"></i></span>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/60 space-y-0.5">
                {categories.map((cat) => (
                  <a key={cat.name} href={cat.url} target="_blank" rel="noopener nofollow" className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer" onClick={closeAll}>
                    <span className="w-5 h-5 flex items-center justify-center"><i className={`${cat.icon} text-sm text-primary-500`}></i></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <button onClick={() => { setMobileArticlesOpen(!mobileArticlesOpen); setMobileCatOpen(false); }} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-article-line text-lg text-primary-500"></i></span>
                Articles
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileArticlesOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileArticlesOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/60 space-y-0.5">
                <Link to="/allchinabuy-spreadsheet-guide" onClick={closeAll} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer">
                  <i className="ri-book-open-line text-sm"></i>
                  Complete Guide (Main)
                </Link>
                {articles.map((a) => (
                  <Link key={a.slug} to={`/article/${a.slug}`} onClick={closeAll} className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                    <span className="text-xs truncate">{a.title.length > 40 ? a.title.slice(0, 40) + '...' : a.title}</span>
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/60 my-2"></div>

            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center justify-center gap-2 mx-4 mt-2 px-5 py-3 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-colors cursor-pointer" onClick={closeAll}>
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}